import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '@/assets/app/css/mypage.module.css';
import useAppAuthStore from '@/shared/store/UseAppAuthStore';
import useAppStore from '@/shared/store/UseAppStore';
import ImgHeaderLevel from '@/assets/app/images/common/header/header-level.png';
import ImgHeaderStar from '@/assets/app/images/common/header/header-star.png';
import ImgHeaderCalendar from '@/assets/app/images/common/header/header-calendar.png';
import defaultPersonImage from '@/assets/app/images/common/default-person.svg';
import mypageBg from '@/assets/app/images/mypage/mypage_bg.webp';
import IconPen from '@/assets/app/images/icon/icon-pen.png';
import IconSettings from '@/assets/app/images/icon/icon-settings.png';
import { getUploadsUrl, openUpdateNicknameSheet, vAppAjax } from '@/shared/utils/CommonUtil';
import { motion } from 'framer-motion';
import useBottomSheetStore from '@/shared/store/UseBottomSheetStore';
import logger from '@/shared/utils/Logger';
import TitleHeader from '../common/TitleHeader';
import { HEADER_ICON_WIDTH, PROFILE_IMAGE_SIZE } from '@/shared/constants/AppIcon';

// 배경 이미지(mypage_bg.webp 1080x1328) 비율
const MYPAGE_BG_RATIO = 1080 / 1328;
// Figma 시안 기준 씨앗 배치 (시안 캡처에서 측정, 2026-11-01)
// - 배경 폭을 레벨 이미지 원본 픽셀로 나타낸 값: 1레벨 씨앗 몸통이 원본 120px, 시안 67px → 배경 폭 354.6px ÷ 0.558
const SEED_BG_WIDTH = 635;
// - 레벨 이미지 아래 끝: 배경 아래에서 배경 높이의 13.6% 위
const SEED_BOTTOM = '13.6%';

const MyPage: React.FC = () => {
  const navigate = useNavigate();
  const { setIsShowBottomNavigation, setIsShowProfileHeader } = useAppStore();
  const [data, setData] = useState<CompletedInfo>();
  const [seedWidth, setSeedWidth] = useState<number>();
  const { member } = useAppAuthStore();

  const loadData = async () => {
    await vAppAjax(
      'get',
      `my/recent-achievement`,
      {},
      (response: AxiosResponse<CommonResult<CompletedInfo>>) => {
        const resultData = response.data;
        if (resultData.code === 200) {
          setData(response.data.result);
        } else {
          logger.warn(resultData.message);
        }
      },
      (error: any) => {
        logger.error(error);
      },
    );
  };

  useEffect(() => {
    setIsShowBottomNavigation(true);
    setIsShowProfileHeader(false);
    loadData();
  }, []);

  return (
    // 높이를 (화면 - 하단 메뉴 80px)로 고정하고, 넘치는 내용은 이 안에서 스크롤된다.
    // 하단 메뉴 높이는 이미 빠져 있으므로 .mypage의 아래 여백(80px)은 40px로 줄여 그만큼 배경 카드가 커지게 한다.
    <div
      className={`page-container page-container--gray ${styles.mypage} flex flex-col overflow-y-auto`}
      style={{ height: 'calc(100dvh - 80px)', minHeight: 0, paddingBottom: 40 }}
    >
      <div className="shrink-0">
        <TitleHeader title="내정보" onBack={() => navigate(-1)}>
          {/* TitleHeader 내부의 children 슬롯으로 쏙 들어갑니다 */}
          <div
            className="cursor-pointer transition-transform active:translate-y-0.5"
            onClick={() => navigate('/mypage/settings')}
          >
            {/* 뒤로가기 버튼(높이 약 32px)과 같은 높이로 보이도록 너비 지정 (원본 202x220) */}
            <img src={IconSettings} width={30} alt="설정" />
          </div>
        </TitleHeader>
      </div>

      {/* 좌우 여백 25px: 배경 카드 폭이 약 95%로 줄어 같은 높이에서 하늘이 더 보이고, 아래 "나의 업적"과 폭이 맞는다 */}
      <main className="relative px-[25px] flex-1 min-h-0 flex flex-col">
        {/* 배경 카드: 기본 높이는 이미지(1080x1328)의 하늘 위쪽을 조금 자른 비율.
            화면이 짧으면 최소 280px까지 줄어들며 하늘이 더 잘리고, 길면 남는 높이를 채운다. */}
        <div
          className="w-full relative rounded-[14px] overflow-hidden grow shrink min-h-[280px]"
          style={{ aspectRatio: '1080 / 1150', containerType: 'size' }}
        >
          {/* 배경 그림 층: background-size: cover와 같게 카드를 덮는 가장 작은 크기로 아래 가운데에 붙인다.
              씨앗을 이 층 기준 %로 두면 카드 높이·화면 폭이 바뀌어도 배경의 같은 자리에 같은 비율로 보인다. */}
          <div
            className="absolute left-1/2 bottom-0 -translate-x-1/2"
            style={{
              width: `max(100cqw, calc(100cqh * ${MYPAGE_BG_RATIO}))`,
              aspectRatio: `${MYPAGE_BG_RATIO}`,
            }}
          >
            <img src={mypageBg} className="w-full h-full" alt="" />
            {/* 레벨 이미지는 성장 단계마다 캔버스 크기가 다르므로(228~318px) 원본 폭에 비례해 그린다 */}
            <img
              src={`${getUploadsUrl()}${member?.levelImageFilename.replace('/uploads', '')}`}
              className="absolute left-1/2 -translate-x-1/2"
              style={{
                bottom: SEED_BOTTOM,
                width: seedWidth ? `${(seedWidth * 100) / SEED_BG_WIDTH}%` : undefined,
                visibility: seedWidth ? 'visible' : 'hidden',
              }}
              onLoad={(e) => setSeedWidth(e.currentTarget.naturalWidth)}
              alt="level"
            />
          </div>

          <div className="relative z-10 w-full px-[25px] mt-3">
            <div className="flex justify-between gap-[16px]">
              <div className="flex-1 flex flex-col items-center justify-center py-[7px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img
                  src={ImgHeaderLevel}
                  width={HEADER_ICON_WIDTH.level}
                  alt="level"
                  className="mb-1"
                />
                <span className="text-[16px] font-extrabold">Lv.{member?.level}</span>
                <span className="text-[11px] font-normal text-[#8D8D8D]">나의 레벨</span>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center py-[7px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img
                  src={ImgHeaderStar}
                  width={HEADER_ICON_WIDTH.star}
                  alt="points"
                  className="mb-1"
                />
                <span className="text-[16px] font-extrabold">{member?.totalPoints}</span>
                <span className="text-[11px] font-normal text-[#8D8D8D]">별포인트</span>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center py-[7px] border border-[#E5E5E5] rounded-[14px] bg-white">
                <img
                  src={ImgHeaderCalendar}
                  width={HEADER_ICON_WIDTH.calendar}
                  alt="calendar"
                  className="mb-1"
                />
                <div className="text-[16px] font-extrabold flex items-baseline">
                  {member?.consecutiveDays}
                  <span className="text-[12px] font-normal ml-0.5">일차</span>
                </div>
                <span className="text-[11px] font-normal text-[#8D8D8D]">연속학습</span>
              </div>
            </div>
          </div>

        </div>
        <div
          className="relative flex items-center mb-2 bg-[#0094EF] shrink-0"
          style={{
            borderBottom: '4px solid #0070D6',
            overflow: 'visible',
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            padding: '8px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginTop: -14,
          }}
        >
          <div
            className="relative -mt-10 shrink-0"
            style={{ width: PROFILE_IMAGE_SIZE, height: PROFILE_IMAGE_SIZE }}
          >
            {/* 1. 프로필 이미지: 부모 크기(w-full h-full)를 100% 채우도록 설정 */}
            <img
              src={
                member?.profileImageFilename
                  ? `${getUploadsUrl()}/character/${member?.profileImageFilename}`
                  : defaultPersonImage
              }
              alt={member?.nickname}
              className="w-full h-full rounded-full object-cover border-0"
            />

            {/* 2. 펜 아이콘: 우측 하단. 이미지가 원형이라 네모 모서리(0,0)에 두면 원과 떨어져 보이므로
                   원 테두리 쪽으로 6% 안으로 들인다 */}
            <div
              style={{ right: '6%', bottom: '6%' }}
              className="absolute w-6 h-6 bg-white border border-[#EBEBEB] flex items-center justify-center rounded-full cursor-pointer shadow-sm"
              onClick={() => navigate('character')}
            >
              {/* 펜 아이콘 크기가 너무 크다면 w-3 h-3 등으로 조절하세요 */}
              <img src={IconPen} className="w-3 h-3 object-contain" alt="edit" />
            </div>
          </div>
          <div className="w-full" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', color: '#fff' }}>
              <span className="text-xl f-one-pop font-bold font-[800]">{member?.nickname}</span>
              <span className="text-[11px]">
                가입일: {new Date(member?.createdAt || '').toLocaleDateString('ko-KR')}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <button
                className="bg-white f-one-pop text-[#0094EF]"
                style={{
                  borderRadius: 25,
                  paddingTop: 6,
                  paddingBottom: 2,
                  fontSize: 13,
                  paddingLeft: 12,
                  paddingRight: 12,
                  borderBottom: '4px solid #B9D3EF',
                }}
                onClick={openUpdateNicknameSheet}
              >
                수정하기
              </button>
            </div>
          </div>
        </div>
      </main>

      <div className="section shrink-0" style={{ margin: '8px 25px 12px' }}>
        <div className="section-header">
          <h3 className="section-header__title" style={{ fontSize: 18, fontWeight: 800 }}>
            나의 업적
          </h3>
          <button
            className="bg-[#F3A9C9] f-one-pop text-white"
            style={{
              borderRadius: 25,
              paddingTop: 6,
              paddingBottom: 2,
              fontSize: 13,
              paddingLeft: 12,
              paddingRight: 12,
              borderBottom: '4px solid #EF73AE',
            }}
            onClick={() => navigate('/mypage/achievements')}
          >
            전체보기
          </button>
        </div>

        {data?.personName && (
          <div
            className={`flex items-center justify-center gap-3 mb-1 mt-2 theme-${data.themeColor}`}
          >
            <span className="px-3 py-0.5 border border-[#F3A9C9] text-[#F3A9C9] text-xs font-bold rounded-full">
              최근
            </span>
            <span className="text-sm font-medium" style={{ color: 'var(--color-btn)' }}>
              {data.categoryName} · {data.personName} · {data.chapterTitle}
            </span>
          </div>
        )}

        <div className="flex items-center justify-center border border-white f-one-pop bg-[#F3A9C9] text-white rounded-[50px] w-100p py-2 mt-2 mx-auto">
          <div className="flex-1 text-center">
            <div className="text-[24px] font-extrabold">
              {data?.completedPerson || 0} / {data?.totalPerson}
            </div>
            <div className="text-[14px]">완료한 인물</div>
          </div>

          <div className="w-[0.5px] h-[42px] bg-[#efefef]"></div>

          <div className="flex-1 text-center">
            <div className="text-[24px] font-extrabold ">
              {data?.completedEra || 0} / {data?.totalEra}
            </div>
            <div className="text-[14px]">완료한 시대</div>
          </div>
        </div>

        {/* {data?.achievements.map((item, idx) => (
          <div
            key={idx}
            className={`bg-white py-4 flex items-center gap-4 theme-${item.themeColor}`}
          >
            <div
              className={`w-20 px-2 py-2 rounded-[25px] flex items-center justify-center text-white font-bold text-sm shrink-0`}
              style={{ background: `var(--color-btn)` }}
            >
              {item.categoryName.replace('시대', '')}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="flex-1 h-[16px] bg-[#DEF0FB] rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(item.completedChapters / item.totalChapters) * 100}%` }}
                    className="h-full rounded-lg"
                    style={{
                      backgroundColor:
                        item.completedChapters === item.totalChapters ? '#F3A9C9' : '#0094EF',
                    }}
                  />
                </div>
                <span className="text-xs font-bold" style={{ color: '#0094EF' }}>
                  {item.completedChapters}/{item.totalChapters}
                </span>
              </div>
            </div>
          </div>
        ))} */}
      </div>
    </div>
  );
};

export default MyPage;
